import { groupHierarchically, WithChildren } from '@upright/common/model/input/blanket/blanket-product/hierarchy-utils';
import { resolveProductWeightsRecursive } from '@upright/common/model/input/companies/products/blanket-ratios';
import { PiuProductState } from '@upright/common/model/input/companies/products/product-input-ui/types';

/**
 * Normalized revenue share (0..1) per node for an *editable* PIU product set
 * (PLT-5692). In the editable flow `revenueShare` is the raw provided fraction
 * the user typed, so we treat it as an exact relative weight — the same
 * interpretation the product edit dialog uses — and run the canonical
 * `resolveProductWeightsRecursive` resolver, which rescales each sibling group
 * so the whole tree sums to 1. A node with no provided value (`undefined`) maps
 * to a `relative` weight of `null`, which the resolver treats as an auto share.
 *
 * The shares are the ones publishing emits. A segment holding no products is
 * not published (PLT-5899): `flattenBlanketProductSet` drops it after weights
 * are resolved over the whole tree and renormalizes what remains across the
 * whole set, so its share is spread over every product, not only its siblings.
 * The same happens here, and a container shows the sum of the products beneath
 * it, which makes an empty segment, and a segment holding only empty segments, 0.
 *
 * Returns a map of product id → normalized share; ids missing from the map
 * (e.g. an empty set) default to 0 at the call site.
 */
export function computeNormalizedShares(products: PiuProductState[]): Map<string, number> {
  const segmentIds = new Set(products.filter(product => product.isSegment === true).map(product => product.id));
  const resolved = resolveProductWeightsRecursive(toWeighted(groupHierarchically(products)));
  const droppedWeight = weightOfEmptySegments(resolved, segmentIds);
  const scale = droppedWeight < 1 ? 1 / (1 - droppedWeight) : 0;
  const shares = new Map<string, number>();
  collect(resolved, segmentIds, scale, shares);
  return shares;
}

type ResolvedNode = WithChildren<Weighted & { resolvedWeight: number }>;

function weightOfEmptySegments(nodes: ResolvedNode[], segmentIds: ReadonlySet<string>): number {
  let total = 0;
  for (const node of nodes) {
    if (node.children.length > 0) {
      total += weightOfEmptySegments(node.children, segmentIds);
    } else if (segmentIds.has(node.id)) {
      total += node.resolvedWeight;
    }
  }
  return total;
}

// The minimal shape `resolveProductWeightsRecursive` needs (`weightType` +
// `weight`), plus `id` so we can key the results back to products.
interface Weighted {
  id: string;
  weight: number | null;
  weightType: 'relative';
}

function toWeighted(nodes: Array<WithChildren<PiuProductState>>): Array<WithChildren<Weighted>> {
  return nodes.map(node => ({
    id: node.id,
    weight: node.revenueShare ?? null,
    weightType: 'relative',
    children: toWeighted(node.children),
  }));
}

// Sets each node's emitted share and returns the total for `nodes`.
function collect(
  nodes: ResolvedNode[],
  segmentIds: ReadonlySet<string>,
  scale: number,
  out: Map<string, number>,
): number {
  let total = 0;
  for (const node of nodes) {
    const share =
      node.children.length > 0
        ? collect(node.children, segmentIds, scale, out)
        : segmentIds.has(node.id)
          ? 0
          : node.resolvedWeight * scale;
    out.set(node.id, share);
    total += share;
  }
  return total;
}
